'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import {
  ACTION_BUTTON_CLASSES,
  CARD_IMAGE_FRAME_CLASSES,
  CARD_TARGET_CLASSES,
} from '../bundle-cards/bundle-card';
import { ClassicPdpUpsellProduct } from '../classic-pdp-content';

export interface UpsellProductCardProps {
  product: ClassicPdpUpsellProduct;
}

/**
 * One linked product of the upsell grid: image, "+" action, name and the price
 * of its default variant. It mirrors the bundle card, so both card types line
 * up in the same grid. A click on the image or the text opens the product page.
 */
export function UpsellProductCard({ product }: Readonly<UpsellProductCardProps>) {
  const t = useTranslations('ClassicProductDetail');
  const { formatCurrency } = useCurrencyFormatter();
  const { localizedLink } = useLocaleLink();
  const productLink = localizedLink(product.path);

  return (
    <article
      data-testid="classic-pdp-upsell-product-card"
      className="relative flex h-full flex-col"
    >
      <div data-testid="classic-pdp-card-image-frame" className={CARD_IMAGE_FRAME_CLASSES}>
        <OptimizedImage
          image={product.image}
          width={300}
          height={300}
          sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 80vw"
          className="mx-auto h-40 w-full object-contain"
        />
        {/* The visitor still has to pick a colour and a cylinder, so the action
            opens the product page instead of adding the default variant. */}
        <Link
          href={productLink}
          aria-label={t('chooseBundle', { name: product.name })}
          className={ACTION_BUTTON_CLASSES}
        >
          +
        </Link>
      </div>

      <h3 className="mt-3 font-bold text-neutral-100">
        <Link href={productLink} className={CARD_TARGET_CLASSES}>
          {product.name}
        </Link>
      </h3>

      {/* Pinned to the bottom so the prices of neighbouring cards line up. */}
      <p className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-1">
        {product.price !== undefined && (
          <span className="font-bold">
            {formatCurrency(product.price, { currency: product.currency })}
          </span>
        )}
      </p>
    </article>
  );
}
